import Close from "@mui/icons-material/Close";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import {
  alpha,
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Portal,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import { type FC, useState } from "react";
import { Link as RouterLink } from "react-router";
import { useSupabase } from "../../../server/supabase/hooks";
import { formatDatetime } from "../../../utils/iso8601";
import {
  splitInsightsByDirection,
  useCommunicationInsightsByMoment,
} from "../hooks/use-communication-insights-by-moment";
import { SupabaseInsightKeyFieldsViewService } from "../server/supabase-insight-key-field-service";
import { InsightsInsightRow } from "./insights-insight-row";

export const InsightsSidePanel: FC<{
  open: boolean;
  onClose: () => void;
  insights: ReturnType<typeof useCommunicationInsightsByMoment>;
}> = ({ open, onClose, insights }) => {
  const { data, loading, error, reload } = insights;
  const { positive, negative, overall_ctr, generatedAt } = splitInsightsByDirection(data ?? []);

  const [fieldDisplayNames, setFieldDisplayNames] = useState<Record<string, string>>({});
  useSupabase(async ({ supabase, account_id }) => {
    try {
      const { data: fields } = await new SupabaseInsightKeyFieldsViewService(supabase).getAll(
        account_id,
        { order: [] },
      );
      if (fields)
        setFieldDisplayNames(Object.fromEntries(fields.map((f) => [f.field_key, f.display_name])));
    } catch {
      // display names unavailable; rows fall back to field_key
    }
  }, []);
  const isError = !loading && error !== null;
  const isEmpty = !loading && !isError && (data ?? []).length === 0;

  return (
    <Portal>
      <Slide direction="left" in={open} mountOnEnter>
        <Paper
          elevation={12}
          sx={(theme) => ({
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: { xs: "100vw", sm: 420 },
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            zIndex: theme.zIndex.drawer + 2,
            borderLeft: `1px solid ${theme.palette.divider}`,
            boxShadow: "0px 10px 50px rgba(15, 23, 42, 0.25)",
            backgroundColor: theme.palette.background.paper,
          })}
        >
          {loading && <LinearProgress />}
          <Stack sx={{ px: 3, py: 2.5 }} gap={1}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LightbulbOutlined fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Insights for this Communication
                </Typography>
              </Stack>
              <IconButton
                aria-label="Close"
                onClick={onClose}
                size="small"
                data-analytics-id="insights-panel-close"
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              We&apos;ve analyzed interactions in this Communication and identified some patterns in
              the data.
            </Typography>
          </Stack>

          <Divider />

          <Stack sx={{ px: 3, py: 2, flex: 1, overflowY: "auto" }} gap={3}>
            {isError ? (
              <Stack gap={1} alignItems="flex-start">
                <Typography variant="body2" color="error">
                  We couldn&apos;t load insights for this Communication.
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Retry"
                  onClick={reload}
                  data-analytics-id="insights-panel-retry"
                >
                  <RefreshOutlined fontSize="small" />
                </IconButton>
              </Stack>
            ) : isEmpty ? (
              <Typography variant="body2" color="text.secondary">
                No insights have been generated yet.
              </Typography>
            ) : (
              <>
                {generatedAt && (
                  <Box
                    sx={(theme) => ({
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderRadius: 1,
                      px: 2,
                      py: 1.5,
                    })}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Typography variant="body2" color="primary.main">
                        Insights generated {formatDatetime(new Date(generatedAt))}
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        fontWeight="bold"
                        onClick={reload}
                        color="primary"
                        data-analytics-id="insights-panel-refresh"
                      >
                        Refresh
                      </Link>
                    </Stack>
                  </Box>
                )}

                {overall_ctr !== null && (
                  <Box
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 2,
                    })}
                  >
                    <Stack alignItems="center" gap={0.5}>
                      <Typography variant="overline" color="text.secondary">
                        Average Engagement
                      </Typography>
                      <Typography variant="h4">{(overall_ctr * 100).toFixed(1)}%</Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Engagement rate across all recipients of this Communication
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {negative.map((insight) => (
                  <Box
                    key={insight.id}
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 2,
                    })}
                  >
                    <Stack spacing={1}>
                      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                        Low Engagement Rate
                      </Typography>
                      <InsightsInsightRow
                        insight={insight}
                        fieldDisplayName={fieldDisplayNames[insight.field_key]}
                      />
                      <Link
                        component={RouterLink}
                        to={`/moments/${insight.moment_id}/analytics?view=engagement&dimension=${encodeURIComponent(insight.field_key)}`}
                        variant="body2"
                        display="block"
                        textAlign="center"
                        data-analytics-id="insights-view-engagement-breakdown"
                      >
                        View engagement breakdown
                      </Link>
                    </Stack>
                  </Box>
                ))}

                {positive.map((insight) => (
                  <Box
                    key={insight.id}
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 2,
                    })}
                  >
                    <Stack spacing={1}>
                      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                        High Engagement Rate
                      </Typography>
                      <InsightsInsightRow
                        insight={insight}
                        fieldDisplayName={fieldDisplayNames[insight.field_key]}
                      />
                      <Link
                        component={RouterLink}
                        to={`/moments/${insight.moment_id}/analytics?view=engagement&dimension=${encodeURIComponent(insight.field_key)}`}
                        variant="body2"
                        display="block"
                        textAlign="center"
                        data-analytics-id="insights-view-engagement-breakdown"
                      >
                        View engagement breakdown
                      </Link>
                    </Stack>
                  </Box>
                ))}
              </>
            )}
          </Stack>
        </Paper>
      </Slide>
    </Portal>
  );
};
